// LiveReplyBubble — in-flight coach reply during token streaming.
//
// Rendered while the SPA is receiving `token` SSE events from the backend
// (MR F). Replaces ReplySkeleton the moment the first token arrives, so
// the user watches text appear as the model writes it instead of staring
// at pipeline-stage copy for 10 seconds.
//
// Deliberately NOT a CoachReplyMessage — no feedback buttons, no citation
// click handlers, no verdict state. Those arrive with the authoritative
// persisted message doc on the `done` event, which replaces this bubble.
// Keeping it a thin render-only component avoids the complexity of
// managing a half-persisted message in Coach.tsx.
//
// Markdown rendering uses the same ReplyDocument component as the final
// bubble so formatting matches up as it grows (headings materialise,
// bullets form, etc.). We pass an empty citations list — inline `[N]`
// markers stay as plain text in the live view and become clickable only
// after swap-in. That's intentional: before `done`, the client has no
// citation metadata to point them at.

import type { CoachCitation } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { ReplyDocument } from './ReplyDocument.js';
import styles from './Coach.module.css';

export interface LiveReplyBubbleProps {
  text: string;
}

const NO_CITATIONS: readonly CoachCitation[] = [];

export function LiveReplyBubble({ text }: LiveReplyBubbleProps) {
  const { t } = useI18n();
  return (
    <div className={styles.rep}>
      <div className={styles.repEyebrow}>{t('coach.author')}</div>
      <ReplyDocument text={text} citations={NO_CITATIONS} onCitationClick={() => undefined} />
      {/* Subtle blinking cursor so the user knows text is still arriving.
          No pulse animation; the growing text itself is the main signal. */}
      <span className={styles.liveCursor} aria-hidden="true">
        ▍
      </span>
    </div>
  );
}
